import type {FilledContentRelationshipField} from '@prismicio/client'
import {asText, isFilled} from '@prismicio/client'
import {PrismicNextLink} from '@prismicio/next/pages'
import type {
  ButtonDocumentData,
  GlossaryPageDocumentData,
  GlossaryTermDocument,
  PickContentRelationshipFieldData,
  Simplify,
} from 'prismicio-types'
import {useMemo} from 'react'
import {ArticleContent} from 'slices/BlogPost/components/ArticleContent'
import {ArticleHeader} from 'slices/BlogPost/components/ArticleHeader'
import {Breadcrumbs} from 'slices/BlogPost/components/Breadcrumbs'
import {CTASection} from 'slices/BlogPost/components/CTASection'
import {TableOfContents} from 'slices/BlogPost/components/TableOfContents'
import {Container} from 'src/common/Container'
import {ThreeColumnLayout} from 'src/common/ThreeColumnLayout'
import {AuthorProfile} from './AuthorProfile'
import {getGlossaryLabels} from './i18n'
import {extractHeadingsFromDefinition} from './utils'

export interface TermContentProps {
  term: GlossaryTermDocument
  parentPage: FilledContentRelationshipField<
    'glossary_page',
    string,
    PickContentRelationshipFieldData<
      {
        id: 'glossary_page'
        fields: [
          {
            id: 'breadcrumbs'
            fields: ['label', 'link']
          },
          'title',
          'related_terms_heading',
        ]
      },
      Simplify<GlossaryPageDocumentData>,
      string
    >
  >
  relatedTerms: Array<GlossaryTermDocument>
}

export function TermContent({term, parentPage, relatedTerms}: TermContentProps) {
  const relatedTermsHeading = isFilled.contentRelationship(parentPage) && parentPage.data?.related_terms_heading

  // Get locale-based labels for consistency across all terms
  const labels = getGlossaryLabels(term.lang)
  const {authorTitle, tocTitle} = labels

  // Extract headings from definition for table of contents
  const headings = useMemo(() => {
    const definitionHeadings = extractHeadingsFromDefinition(term.data.definition)

    // Add Related Terms heading if there are related terms
    if (relatedTerms.length > 0) {
      return [
        ...definitionHeadings,
        ...(relatedTermsHeading
          ? [
              {
                id: 'related-terms',
                text: relatedTermsHeading,
              },
            ]
          : []),
      ]
    }

    return definitionHeadings
  }, [term.data.definition, relatedTerms.length, relatedTermsHeading])

  const hasAuthor = isFilled.contentRelationship(term.data.author)
  const hasTOC = headings.length > 0
  const ctaSectionData = isFilled.group(term.data.cta_section) ? term.data.cta_section[0] : null

  // Extract all section data for ArticleContent
  const takeawaysSection = isFilled.group(term.data.takeaways_section) ? term.data.takeaways_section : []
  const bannerSection = isFilled.group(term.data.banner) ? term.data.banner : []
  const faqSection = isFilled.group(term.data.faq_section) ? term.data.faq_section : []
  const listSection = isFilled.group(term.data.list_section) ? term.data.list_section : []
  const tableSection = isFilled.group(term.data.table) ? term.data.table : []
  const quotesSection = isFilled.group(term.data.quotes) ? term.data.quotes : []

  // Extract button data for CTA section
  const buttonData =
    ctaSectionData && isFilled.contentRelationship(ctaSectionData.button) && ctaSectionData.button.data
      ? (ctaSectionData.button.data as ButtonDocumentData)
      : null

  // const hasCTA = buttonData !== null

  const formatDate = (date: string | null | undefined) => {
    if (!date) {
      return null
    }

    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const publishedDate = formatDate(term.data.published_date) || ''

  return (
    <Container className="!overflow-visible !pt-12 md:!bg-moss-grey-100 md:!pt-[120px]">
      <div className="[1100px]:pb-20 mx-auto w-full max-w-[1200px] rounded-[4px] bg-moss-white px-5 pb-5 pt-12 max-[1100px]:mx-auto max-[1100px]:w-fit md:pb-10 md:max-[1100px]:px-10 lg:px-5 xl:px-0">
        {/* Three-column layout: TOC | Content | CTA */}
        <ThreeColumnLayout
          leftSidebar={hasTOC ? <TableOfContents headings={headings} title={tocTitle} /> : <></>}
          rightSidebar={
            ctaSectionData ? (
              <CTASection
                buttonData={buttonData}
                ctaTitle={ctaSectionData.title}
                rating={ctaSectionData.score}
                ratingPlatform={ctaSectionData.rating_platform}
                pageUid={term.uid}
              />
            ) : (
              <></>
            )
          }
          mainContentClassName={`flex-1 flex flex-col gap-6 ${
            hasTOC || ctaSectionData ? 'max-w-[720px]' : 'max-w-3xl'
          }`}
          mainContent={
            <>
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-1">
                  <Breadcrumbs parentPage={parentPage} title={asText(term.data.term)} />
                </div>
                <ArticleHeader
                  title={term.data.term}
                  formattedDate={publishedDate}
                  author={term.data.author}
                  authorTag={authorTitle}
                />
              </div>

              <div>
                <div className="flex flex-col gap-6 rounded-lg border border-moss-black/[10%] bg-moss-white p-6 min-[1100px]:hidden">
                  <TableOfContents headings={headings} title={tocTitle} />
                </div>

                <div className="flex flex-col gap-4" id="article-content">
                  <ArticleContent
                    content={term.data.definition}
                    sections={{
                      takeaways: takeawaysSection,
                      list: listSection,
                      banner: bannerSection,
                      faq: faqSection,
                      table: tableSection,
                      quotes: quotesSection,
                    }}
                  />
                </div>
              </div>

              {/* Author Profile (if present) */}
              {hasAuthor && (
                <div className="my-12">
                  <AuthorProfile author={term.data.author} authorTitle={authorTitle} />
                </div>
              )}

              {/* Related Terms Section */}
              {relatedTerms.length > 0 && (
                <section className="border-t border-dcdbda pt-8">
                  {relatedTermsHeading && (
                    <h2 id="related-terms" className="mb-4 text-24 font-semibold text-2a2928 md:text-28">
                      {relatedTermsHeading}
                    </h2>
                  )}
                  <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {relatedTerms.map((relatedTerm) => (
                      <li key={relatedTerm.uid}>
                        <PrismicNextLink
                          document={relatedTerm}
                          className="block rounded-lg border border-dcdbda p-4 transition-colors hover:border-[#5a7269]"
                        >
                          <h3 className="text-18 font-medium text-2a2928">{asText(relatedTerm.data.term)}</h3>
                        </PrismicNextLink>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          }
        />
      </div>
    </Container>
  )
}
